import type { ReactNode } from "react";

type Block = { title: string; body: ReactNode };

const BLOCKS: Block[] = [
  {
    title: "cohort",
    body: (
      <p>
        The locked baseline. Active users who signed up between <code>now() - 180d</code> and{" "}
        <code>now() - 30d</code>, excluding: support roles; @gomarloo accounts; users whose
        company already had a paying subscription within 1 hour of sign-up (instant paid);
        and users whose company has had any non-consumer-email-domain user attend a demo.
      </p>
    ),
  },
  {
    title: "consumer_domains",
    body: (
      <p>
        When checking whether a company has had a demo, attendees on common consumer email
        providers (gmail, yahoo, outlook, icloud, NZ/AU/UK ISP domains, etc.) are ignored.
        Two adviser shops sharing a gmail.com domain are not actually the same company, so
        their demos must not exclude each other from the baseline.
      </p>
    ),
  },
  {
    title: "company_founders",
    body: (
      <p>
        For each company we take the earliest active user as the &ldquo;founder&rdquo;.
        Any other user in that company is flagged <code>is_team_invite = true</code>. This
        is the proxy for the first-company-signup vs team-invite filter on the page; it&apos;s
        a heuristic until eng exposes an explicit signup-source flag.
      </p>
    ),
  },
  {
    title: "nth-event dates",
    body: (
      <p>
        The granular thresholds in the feature heatmap (2+ summaries, 5+ summaries, etc.) need
        per-user nth-event timestamps. We get them with <code>array_agg(... ORDER BY ...)[n]</code> in
        the aggregate CTEs — cheap, no extra rows, but the array materialisation does mean the
        CTE is unbounded in width on power users. If the cohort grows past ~10k users, switch
        to <code>percentile_cont</code> or move to <code>vw_onboarding_user_events</code>.
      </p>
    ),
  },
  {
    title: "denominators in the heatmap",
    body: (
      <p>
        Each weekly column uses only users with enough observation: a user who signed up 35
        days ago is in the denominator for Week 1–4 but not Week 8 or 12. The bottom row of
        the heatmap shows that effective denominator per column. The &ldquo;Ever&rdquo; column
        uses the full cohort because every user has had at least 30 days of observation.
      </p>
    ),
  },
  {
    title: "billing.subscription_started_date_utc",
    body: (
      <p>
        We pick <code>MIN(subscription_started_date_utc)</code> filtered to plus/pro. The
        billing table can have multiple rows per company (cancelled → re-subscribed → upgraded),
        so we deliberately collapse to the first transition to paid.
      </p>
    ),
  },
];

export function OnboardingBaselineExplain() {
  return (
    <div style={{ display: "grid", gap: 16, padding: 16 }}>
      {BLOCKS.map((b) => (
        <section key={b.title}>
          <h3 style={{ margin: "0 0 4px", fontSize: 14 }}>{b.title}</h3>
          <div style={{ color: "var(--fg-2)", fontSize: 13, lineHeight: 1.5 }}>{b.body}</div>
        </section>
      ))}
    </div>
  );
}

export function OnboardingBaselineSchema() {
  return (
    <div style={{ padding: 16, color: "var(--fg-2)", fontSize: 13, lineHeight: 1.6 }}>
      <p style={{ marginTop: 0 }}>
        Spine is <code>cohort</code> (a CTE that bakes the six baseline rules onto
        <code> public.user_roles</code>). One row per (user, company) pair.
      </p>
      <ul style={{ paddingLeft: 18 }}>
        <li><strong>Cohort filters</strong>: <code>public.user_roles</code> (status=active, role≠support) → <code>public.users</code> (180d window, not @gomarloo) ← exclusion vs <code>companies_with_demo</code> ← <code>reporting.demo</code> with consumer-domain mask; and exclusion vs <code>billing_first_paid</code> (<code>public.company_billing</code> ≤ signup+1h).</li>
        <li><strong>Activity aggregates</strong> (per user × company):
          <ul>
            <li><code>transcripts</code> ← <code>public.meeting_transcription</code> × <code>public.meeting</code> (is_demo=false).</li>
            <li><code>synopses</code> ← <code>public.meeting_synopsis</code> × <code>public.meeting</code> (is_demo=false). Exposes 1st / 2nd / 5th synopsis dates via <code>array_agg</code>.</li>
            <li><code>custom_templates</code> ← <code>public.template</code> (not auto-provisioned, not archived). Exposes 1st / 2nd template dates.</li>
            <li><code>calendar_first</code> ← <code>public.calendar</code>.</li>
            <li><code>chats</code> ← <code>public.chat</code>. Split into meeting-keyed and client-keyed via FILTER.</li>
            <li><code>documents</code> ← <code>public.document</code>. Exposes 1st / 3rd document dates.</li>
          </ul>
        </li>
        <li><strong>Company-level joins</strong>: <code>public.companies</code> (country_code), <code>company_size</code> (active-user count), <code>billing</code> (on_paid_plan, billing_plan, subscription_started_date_utc), <code>company_founders</code> (earliest active user → is_team_invite).</li>
      </ul>
      <p style={{ color: "var(--fg-3)" }}>
        A proper boxes-and-arrows diagram lives in <code>schemaConfig.ts</code> on the
        sign-up funnel; this dashboard inherits the same tables, so reuse that as a
        visual reference if you need one.
      </p>
    </div>
  );
}
